import React, { useState, useEffect } from "react";
import styles from "../assets/css/UserList.module.css";
import common from "../assets/css/common.module.css";
import { Loader } from "../../components/Loader";
import { NavLink, useNavigate } from "react-router-dom";
import { postData1 } from "../../services";
import { Pagination } from "../Common";

// Add GET request function for failed payments
const fetchFailedPayments = async (page = 1, limit = 20) => {
  const BaseURL = process.env.REACT_APP_BASEURL;
  const token = localStorage.getItem("adminAuth");
  
  try {
    const response = await fetch(`${BaseURL}/product/failed-payments/?page=${page}&page_size=${limit}`, {
      method: "GET",
      headers: {
        "Accept": "application/json",
        "Authorization": `Token ${token}`,
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    return result;
  } catch (error) {
    console.error("GET request error:", error);
    throw error;
  }
};

export const FailedPayments = () => {
  const navigate = useNavigate();
  const [failedPaymentsData, setFailedPaymentsData] = useState([]);
  const [sortedData, setSortedData] = useState([]);
  const [loader, setLoader] = useState(true);
  const [currPage, setCurrPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [paging, setPaging] = useState({});
  const [prevPage, setPrevPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  // Add immediate console log when component loads
  console.log("🚀 FAILED PAYMENTS PAGE LOADED!");
  console.log("📊 Current page:", currPage);
  console.log("🔧 Loader state:", loader);

  useEffect(() => {
    console.log("🔄 useEffect triggered - Starting API call...");
    (async () => {
      try {
        console.log("📡 CALLING API: product/failed-payments/ (GET)");
        console.log("⚙️ API Config: page=", currPage, "limit=", perPage);
        
        const res = await fetchFailedPayments(currPage, perPage);
        
        console.log("✅ API RESPONSE RECEIVED!");
        console.log("📦 Full response:", res);
        console.log("📝 Response as JSON:", JSON.stringify(res, null, 2));
        
        if (res) {
          console.log("🔍 Response Analysis:");
          console.log("   - Type:", typeof res);
          console.log("   - Keys:", Object.keys(res));
          console.log("   - Has data property:", !!res?.data);
          console.log("   - Has results property:", !!res?.results);
          console.log("   - Has count property:", !!res?.count);
          
          if (res?.data) {
            console.log("📂 Data property analysis:");
            console.log("   - Data type:", typeof res.data);
            console.log("   - Data keys:", Object.keys(res.data));
            console.log("   - Has results:", !!res.data?.results);
            console.log("   - Results length:", res.data?.results?.length);
            console.log("   - Results sample:", res.data?.results?.[0]);
          }
          
          if (res?.results) {
            console.log("📋 Results property analysis:");
            console.log("   - Results type:", typeof res.results);
            console.log("   - Is array:", Array.isArray(res.results));
            console.log("   - Results length:", res.results?.length);
            console.log("   - First result:", res.results?.[0]);
          }
        }
        
        // Handle different response formats
        const results = res?.results || res?.data?.results || res?.data || [];
        console.log("🎯 FINAL RESULTS TO USE:");
        console.log("   - Results:", results);
        console.log("   - Type:", typeof results);
        console.log("   - Is array:", Array.isArray(results));
        console.log("   - Length:", results?.length || 0);
        
        if (results.length > 0) {
          console.log("📄 First result sample:", results[0]);
        } else {
          console.log("⚠️ NO RESULTS FOUND!");
        }
        
        setFailedPaymentsData(results);
        setPaging({
          total: res?.count || res?.total || results.length,
          total_pages: res?.total_pages || 1,
        });
        setLoader(false);
        
        console.log("🏁 API call completed successfully!");
        console.log("📊 Final state - Results length:", results.length);
        console.log("📊 Final state - Total:", res?.count || results.length);
        
      } catch (error) {
        console.error("❌ API ERROR!");
        console.error("🔥 Error message:", error.message);
        console.error("🔥 Error details:", error);
        console.error("🔥 Stack trace:", error.stack);
        setFailedPaymentsData([]);
        setPaging({ total: 0, total_pages: 1 });
        setLoader(false);
      }
    })();
  }, [currPage, perPage]);

  // Apply sorting to the whole dataset
  useEffect(() => {
    let sortedData = Array.isArray(failedPaymentsData) ? [...failedPaymentsData] : [];
    if (sortConfig.key) {
      sortedData.sort((a, b) => {
        const valA = a[sortConfig.key] ?? "";
        const valB = b[sortConfig.key] ?? "";

        if (typeof valA === "string") {
          return sortConfig.direction === "asc"
            ? valA.localeCompare(valB)
            : valB.localeCompare(valA);
        } else {
          return sortConfig.direction === "asc" ? valA - valB : valB - valA;
        }
      });
    }
    setSortedData(sortedData);
  }, [failedPaymentsData, sortConfig]);

  const updatePage = (e) => {
    let name = e.currentTarget.name;
    if (name === "prev") {
      setCurrPage(currPage === 1 ? 1 : currPage - 1);
      setPrevPage(currPage);
    } else if (name === "next") {
      setCurrPage(currPage === paging?.total_pages ? currPage : currPage + 1);
      setPrevPage(currPage);
    } else {
      setCurrPage(parseInt(e.currentTarget.innerHTML));
    }
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return "⇅";
    return sortConfig.direction === "asc" ? "▲" : "▼";
  };

  // Paginate the sorted data
  const paginateData = (data) => {
    if (!Array.isArray(data)) return [];
    return data;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString();
  };

  return (
    <section className={styles.userListCont}>
      <div className={styles.listHead}>
        <h1>FAILED PAYMENTS</h1>
      </div>
      <div className={styles.dataItems} style={{ overflowX: "auto", maxWidth: "100%" }}>
        <div className={styles.itemHead} style={{ minWidth: "1800px" }}>
          <span onClick={() => handleSort("created_at")} style={{ minWidth: "100px" }}>
            DATE {getSortIcon("created_at")}
          </span>
          <span onClick={() => handleSort("created_at")} style={{ minWidth: "80px" }}>
            TIME {getSortIcon("created_at")}
          </span>
          <span onClick={() => handleSort("buyer_name")} style={{ minWidth: "120px" }}>
            BUYER NAME {getSortIcon("buyer_name")}
          </span>
          <span onClick={() => handleSort("buyer_email")} style={{ minWidth: "150px" }}>
            BUYER EMAIL {getSortIcon("buyer_email")}
          </span>
          <span onClick={() => handleSort("buyer_phone")} style={{ minWidth: "120px" }}>
            BUYER PHONE {getSortIcon("buyer_phone")}
          </span>
          <span onClick={() => handleSort("buyer_address")} style={{ minWidth: "150px" }}>
            BUYER ADDRESS {getSortIcon("buyer_address")}
          </span>
          <span onClick={() => handleSort("product_details.equip_name")} style={{ minWidth: "200px" }}>
            PRODUCT NAME {getSortIcon("product_details.equip_name")}
          </span>
          <span onClick={() => handleSort("product_details.asking_price")} style={{ minWidth: "120px" }}>
            PRODUCT PRICE {getSortIcon("product_details.asking_price")}
          </span>
          <span onClick={() => handleSort("amount")} style={{ minWidth: "120px" }}>
            PAYMENT AMOUNT {getSortIcon("amount")}
          </span>
          <span onClick={() => handleSort("razorpay_order_id")} style={{ minWidth: "150px" }}>
            ORDER ID {getSortIcon("razorpay_order_id")}
          </span>
          <span onClick={() => handleSort("razorpay_error_code")} style={{ minWidth: "100px" }}>
            ERROR CODE {getSortIcon("razorpay_error_code")}
          </span>
          <span onClick={() => handleSort("razorpay_error_description")} style={{ minWidth: "200px" }}>
            ERROR DESCRIPTION {getSortIcon("razorpay_error_description")}
          </span>
          <span onClick={() => handleSort("notes")} style={{ minWidth: "250px" }}>
            FAILURE NOTES {getSortIcon("notes")}
          </span>
        </div>

        {/* Debug logging */}
        {console.log("Render - failedPaymentsData:", failedPaymentsData)}
        {console.log("Render - sortedData:", sortedData)}
        {console.log("Render - loader:", loader)}

        {sortedData.length === 0 && !loader ? (
          <div style={{ textAlign: "center", padding: "40px", color: "#666" }}>
            <h3>No Failed Payments Found</h3>
            <p>There are currently no failed payment transactions to display.</p>
          </div>
        ) : (
          <div style={{ minWidth: "1800px" }}>
            {sortedData.map((data, index) => (
              <div className={styles.itemData} key={data.uid || index}>
                <span style={{ wordWrap: "break-word", whiteSpace: "normal", minWidth: "100px" }}>
                  {formatDate(data?.created_at)}
                </span>
                <span style={{ wordWrap: "break-word", whiteSpace: "normal", minWidth: "80px" }}>
                  {formatTime(data?.created_at)}
                </span>
                <span style={{ wordWrap: "break-word", whiteSpace: "normal", minWidth: "120px" }}>
                  {data?.buyer_name || "N/A"}
                </span>
                <span style={{ wordWrap: "break-word", whiteSpace: "normal", minWidth: "150px" }}>
                  {data?.buyer_email || "N/A"}
                </span>
                <span style={{ wordWrap: "break-word", whiteSpace: "normal", minWidth: "120px" }}>
                  {data?.buyer_phone || "N/A"}
                </span>
                <span style={{ wordWrap: "break-word", whiteSpace: "normal", minWidth: "150px" }}>
                  {data?.buyer_address || "N/A"}
                </span>
                <span style={{ wordWrap: "break-word", whiteSpace: "normal", minWidth: "200px" }}>
                  {data?.product_details?.equip_name || "N/A"}
                </span>
                <span style={{ wordWrap: "break-word", whiteSpace: "normal", minWidth: "120px" }}>
                  ₹{data?.product_details?.asking_price || "N/A"}
                </span>
                <span style={{ wordWrap: "break-word", whiteSpace: "normal", minWidth: "120px" }}>
                  ₹{data?.amount || "0.00"}
                </span>
                <span style={{ wordWrap: "break-word", whiteSpace: "normal", minWidth: "150px" }}>
                  {data?.razorpay_order_id || "N/A"}
                </span>
                <span style={{ wordWrap: "break-word", whiteSpace: "normal", minWidth: "100px", color: "red", fontWeight: "bold" }}>
                  {data?.razorpay_error_code || "N/A"}
                </span>
                <span style={{ wordWrap: "break-word", whiteSpace: "normal", minWidth: "200px", fontSize: "11px", color: "#666" }}>
                  {data?.razorpay_error_description || "No description"}
                </span>
                <span style={{ wordWrap: "break-word", whiteSpace: "normal", minWidth: "250px", fontSize: "10px", color: "#333" }}>
                  {data?.notes || "No notes"}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
      <Pagination
        total={paging?.total}
        perPage={perPage}
        setPerPage={setPerPage}
        currPage={currPage}
        setCurrPage={setCurrPage}
        prevPage={prevPage}
        tPage={paging?.total_pages}
        updatePage={updatePage}
      />
      {loader && <Loader />}
    </section>
  );
};
